import { useInfiniteQuery, useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { fetchAllHydraMembers } from "src/api/_common/hydra/fetchAllHydraMembers";

export const TemplateSsqVersionSchema = z.object({
    id: z.number().int().positive(),
    versionMajor: z.number().int().nonnegative(),
    versionMinor: z.number().int().nonnegative(),
    status: z.number().int(),
});
const SsqOptionSchema = z.object({
    id: z.number(),
    title: z.string(),
    currentVersion: TemplateSsqVersionSchema.nullish(),
    versions: TemplateSsqVersionSchema.array(),
});
const SSQ_OPTIONS_PAGE_SIZE = 20;
const SSQ_OPTIONS_STALE_TIME_MS = 5 * 60 * 1000;
const SsqPageSchema = z.object({
    "hydra:member": SsqOptionSchema.array(),
    "hydra:totalItems": z.number().int().nonnegative(),
});
const SectorOptionSchema = z.object({
    id: z.number(),
    name: z.string(),
    ssqs: z.array(z.object({ ssqId: z.number(), position: z.number() })),
});
const SECTOR_OPTIONS_PAGE_SIZE = 20;
const SectorPageSchema = z.object({
    "hydra:member": SectorOptionSchema.array(),
    "hydra:totalItems": z.number().int().nonnegative(),
});
export type TemplateSectorOption = z.infer<typeof SectorOptionSchema>;
export type TemplateSsqOption = z.infer<typeof SsqOptionSchema>;
export type TemplateSsqVersion = z.infer<typeof TemplateSsqVersionSchema>;

export const useTemplateSectorPages = (ssqId?: number, sectorSearch = "") => {
    const { i18n } = useTranslation();
    const sectorQuery = useInfiniteQuery({
        queryKey: ["actionPlanTemplateSectorPages", i18n.resolvedLanguage, ssqId, sectorSearch.trim()],
        keepPreviousData: false,
        queryFn: async ({ pageParam = 1, signal }) => {
            const response = await loggedInApiCall.get("/api/organization_sectors", {
                signal,
                params: {
                    itemsPerPage: SECTOR_OPTIONS_PAGE_SIZE,
                    page: pageParam,
                    ...(ssqId === undefined ? {} : { "organizationSectorSsqs.ssqId": ssqId }),
                    ...(sectorSearch.trim() ? { name: sectorSearch.trim() } : {}),
                },
            });
            return SectorPageSchema.parse(response.data);
        },
        getNextPageParam: (lastPage, pages) =>
            pages.reduce((count, page) => count + page["hydra:member"].length, 0) < lastPage["hydra:totalItems"] ?
                pages.length + 1
            :   undefined,
    });
    return {
        ...sectorQuery,
        data: sectorQuery.data?.pages.flatMap((page) => page["hydra:member"]),
        refetch: () =>
            sectorQuery.hasNextPage && sectorQuery.data ? sectorQuery.fetchNextPage() : sectorQuery.refetch(),
    };
};

export const useTemplateCreationOptions = (ssqSearch = "", sectorSearch = "", ssqId?: number, sectorId?: number) => {
    const { i18n } = useTranslation();
    const sectors = useTemplateSectorPages(ssqId, sectorSearch);
    const associations = useQuery({
        queryKey: ["actionPlanTemplateSectorAssociations", i18n.resolvedLanguage],
        staleTime: SSQ_OPTIONS_STALE_TIME_MS,
        queryFn: async ({ signal }) =>
            SectorOptionSchema.array().parse(
                await fetchAllHydraMembers({
                    client: loggedInApiCall,
                    endpoint: "/api/organization_sectors",
                    requestConfig: { signal },
                }),
            ),
    });
    const eligibleSectors =
        sectorId === undefined ?
            (associations.data ?? [])
        :   (associations.data?.filter((sector) => sector.id === sectorId) ?? []);
    const allowedSsqIds = [
        ...new Set(eligibleSectors.flatMap((sector) => sector.ssqs.map((association) => association.ssqId))),
    ].sort((a, b) => a - b);
    const ssqQuery = useInfiniteQuery({
        queryKey: ["actionPlanTemplateSsqPages", i18n.resolvedLanguage, ssqSearch.trim(), allowedSsqIds],
        enabled: associations.isSuccess && allowedSsqIds.length > 0,
        keepPreviousData: false,
        staleTime: SSQ_OPTIONS_STALE_TIME_MS,
        queryFn: async ({ pageParam = 1, signal }) => {
            const response = await sureApiCall.get("/api/ssqs", {
                signal,
                params: {
                    "ssqTargetEntities.entityType": "company",
                    actionPlanAvailable: true,
                    itemsPerPage: SSQ_OPTIONS_PAGE_SIZE,
                    page: pageParam,
                    ...(ssqSearch.trim() ? { search: ssqSearch.trim() } : {}),
                    ...Object.fromEntries(allowedSsqIds.map((id, index) => [`id[${index}]`, id])),
                },
            });
            const page = SsqPageSchema.parse(response.data);
            if (pageParam > 1 && page["hydra:member"].length === 0) {
                throw new Error("SSQ pagination returned an empty page before reaching the total");
            }
            return page;
        },
        getNextPageParam: (lastPage, pages) =>
            pages.reduce((count, page) => count + page["hydra:member"].length, 0) < lastPage["hydra:totalItems"] ?
                pages.length + 1
            :   undefined,
    });
    const ssqs = {
        ...ssqQuery,
        data: ssqQuery.data?.pages.flatMap((page) => page["hydra:member"]),
        refetch: () => (ssqQuery.hasNextPage && ssqQuery.data ? ssqQuery.fetchNextPage() : ssqQuery.refetch()),
    };
    return { ssqs, sectors, associations };
};
